/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  OwnUpComponentLibraryProvider,
  OwnUpDropdown,
  OwnUpFillButtonPrimary,
  OwnUpGridContainer,
  OwnUpGridItem,
  OwnUpGridWrapper,
  OwnUpHeadlineBook,
  OwnUpMenuItem,
  OwnUpSpinner,
  OwnUpTextInput
} from '@rategravity/own-up-component-library';
import { RuleProperties, TopLevelCondition } from 'json-rules-engine/types';
import { useEffect, useState } from 'react';
import React from 'react';
import { fetchAllLenderBudgetsApi, updateLenderBudgetApi } from '../../modules/api/lender-budget';
import {
  fetchLenderConditionsApi,
  LenderRuleResult,
  updateLenderRulesApi
} from '../../modules/api/lender-rules';
import { LenderBudget } from '../../redux/lender-configuration/types';

export const NewLenderCreationPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [createLenderLoading, setCreateLenderLoading] = useState(false);
  const [lenderRules, setLenderRules] = useState<LenderRuleResult | null>(null);
  const [lenderBudget, setLenderBudget] = useState<LenderBudget[] | undefined>(undefined);
  const [newLender, setNewLender] = useState({
    lenderId: '',
    categorization: 'National',
    pricingTier: 'T3'
  });
  const [lenderIdError, setLenderIdError] = useState(false);
  useEffect(() => {
    // On page load, fetch the template lender rules
    void (async () => {
      const [templateRules, budgets] = await Promise.all([
        fetchLenderConditionsApi('templateLender'),
        fetchAllLenderBudgetsApi()
      ]);
      setLenderRules(templateRules);
      const templateBudgets = budgets.filter((budget) => budget.lenderId === 'templateLender');
      const budgetsWithoutCategorization = templateBudgets.map((budget) => {
        const index = budget.categorization.indexOf('National');
        if (index > -1) {
          budget.categorization.splice(index, 1);
        }
        return budget;
      });

      setLenderBudget(budgetsWithoutCategorization);
    })();
    setIsLoading(false);
  }, []);

  const createNewLender = async () => {
    await updateLenderRulesApi({
      lenderId: newLender.lenderId,
      comment: `Creating new lender ${newLender.lenderId} from template`,
      rules: lenderRules?.rules?.reduce(
        (acc, rule) => {
          acc[rule.name as keyof typeof acc] = {
            ...rule,
            event: {
              ...rule.event,
              params: {
                ...rule.event.params,
                score:
                  newLender.categorization === 'Regional'
                    ? rule.event.params?.score + 1
                    : rule.event.params?.score,
                leadCost: (() => {
                  if (typeof rule.event.params?.leadCost === 'number') {
                    return rule.event.params.leadCost as number;
                  } else {
                    return {
                      ...rule.event.params?.leadCost,
                      params: {
                        ...rule.event.params?.leadCost?.params,
                        tier: rule.event.params?.leadCost?.params?.tier?.replace(
                          /^T3/,
                          newLender.pricingTier
                        )
                      }
                    };
                  }
                })()
              }
            }
          };
          return acc;
        },
        {} as Record<string, Omit<RuleProperties, 'name' | 'onSuccess' | 'onFailure'>>
      ),
      conditions: lenderRules?.conditions?.reduce(
        (acc, item) => {
          acc[item.name] = item.conditions;
          return acc;
        },
        {} as Record<string, TopLevelCondition>
      )
    });

    const updatedBudgets = lenderBudget?.reduce<LenderBudget[]>((acc, budget) => {
      acc.push({
        ...budget,
        lenderId: newLender.lenderId,
        categorization: [...budget.categorization, newLender.categorization]
      });
      return acc;
    }, []);
    for (const budget of updatedBudgets!) {
      await updateLenderBudgetApi(budget);
    }
  };

  const handleCreateLender = async () => {
    setCreateLenderLoading(true);
    if (!newLender.lenderId) {
      setLenderIdError(true);
      setCreateLenderLoading(false);
      return;
    }
    // Create a new lender with the same rules as the template lender
    await createNewLender();
    // Redirect to the new lender configuration page
    setCreateLenderLoading(false);
    window.location.href = `/lender-configuration/${newLender.lenderId}`;
  };

  return (
    <OwnUpComponentLibraryProvider>
      <OwnUpGridWrapper>
        <OwnUpGridContainer variant="slim">
          <OwnUpGridItem xs={12}>
            <OwnUpHeadlineBook variant="h1" style={{ textAlign: 'center' }}>
              New Lender Creation
            </OwnUpHeadlineBook>
            <br />
          </OwnUpGridItem>
          {isLoading ? (
            <OwnUpGridItem xs={12}>
              <OwnUpSpinner />
            </OwnUpGridItem>
          ) : (
            <OwnUpGridItem
              xs={12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              {createLenderLoading ? (
                <OwnUpSpinner />
              ) : (
                <>
                  <OwnUpTextInput
                    label="Lender Id"
                    error={lenderIdError}
                    labelPosition="inner"
                    aria-label="lenderId"
                    style={{ border: '1px solid #000', width: '100%' }}
                    onChange={(e) => setNewLender({ ...newLender, lenderId: e.target.value })}
                    value={newLender.lenderId}
                  />
                  <OwnUpDropdown
                    label="Categorization"
                    value={newLender.categorization}
                    onChange={(event) => {
                      setNewLender({
                        ...newLender,
                        categorization: event.target.value as 'National' | 'Regional'
                      });
                    }}
                  >
                    <OwnUpMenuItem value="National">National</OwnUpMenuItem>
                    <OwnUpMenuItem value="Regional">Regional</OwnUpMenuItem>
                  </OwnUpDropdown>
                  <OwnUpDropdown
                    label="Pricing Tier"
                    value={newLender.pricingTier}
                    onChange={(event) => {
                      setNewLender({
                        ...newLender,
                        pricingTier: event.target.value as 'T1' | 'T2' | 'T3'
                      });
                    }}
                  >
                    <OwnUpMenuItem value="T1">T1</OwnUpMenuItem>
                    <OwnUpMenuItem value="T2">T2</OwnUpMenuItem>
                    <OwnUpMenuItem value="T3">T3</OwnUpMenuItem>
                  </OwnUpDropdown>
                </>
              )}
              <OwnUpFillButtonPrimary onClick={handleCreateLender}>
                Create New Lender
              </OwnUpFillButtonPrimary>
            </OwnUpGridItem>
          )}
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
    </OwnUpComponentLibraryProvider>
  );
};
